import { TabConfiguration, ColumnConfig } from './configBuilder';
import {
  PageConfig,
  ColumnConfig as TableColumnConfig,
  FilterConfiguration,
  FilterGroup,
} from 'components/LMA/types';

/**
 * Convert Sitecore column configuration to table column configuration
 * Adds system columns: expand (for expandable rows) and lock (for record locking)
 */
export const convertToTableColumns = (columns: ColumnConfig[]): TableColumnConfig[] => {
  const systemColumns: TableColumnConfig[] = [
    {
      id: 'expand',
      label: '',
      accessor: 'expand',
      sortable: false,
      width: '50px',
      align: 'left' as const,
    },
  ];

  const dataColumns: TableColumnConfig[] = columns.map((col) => ({
    ...col,
    id: col.id,
    label: col.label,
    accessor: col.field,
    sortable: col.sortable,
    width: col.width?.toString(),
    maxWidth: '300px',
    align: 'left' as const,
  }));

  // Lock column (added at the end, conditionally hidden via hideLockColumn prop)
  const lockColumn: TableColumnConfig = {
    id: 'lock',
    label: 'Lock',
    accessor: 'lock',
    sortable: false,
    width: '80px',
    align: 'center' as const,
  };

  // Patient Type column (shows ChildCare icon for minor patients)
  const patientTypeColumn: TableColumnConfig = {
    id: 'patientType',
    label: '',
    accessor: 'patientType',
    sortable: false,
    width: '50px',
    align: 'center' as const,
  };

  return [...systemColumns, ...dataColumns, lockColumn, patientTypeColumn];
};

/**
 * Convert Sitecore filter configuration to filter panel configuration
 */
export const convertToFilterConfig = (tabConfig: TabConfiguration | null): FilterConfiguration => {
  const filterGroups: FilterGroup[] = [];

  if (!tabConfig) {
    return {
      enabled: true,
      filterLabel: 'Filters',
      applyButtonText: 'Apply Filters',
      clearButtonText: 'Clear All',
      filterGroups: [],
    };
  }
  // Combined Search Filters Group: preserve original tabConfig.filters order for displayOrder
  const combinedFields: any[] = [];

  tabConfig.filters.forEach((filter, index) => {
    const fieldMapping = filter.id;
    const displayOrder = index + 1;

    if (filter.type === 'text') {
      combinedFields.push({
        id: filter.id,
        label: filter.label,
        type: 'text' as const,
        enabled: true,
        displayOrder,
        placeholder: `Enter ${filter.label}`,
        field: fieldMapping,
      });
    } else if (filter.type === 'select') {
      combinedFields.push({
        id: filter.id,
        label: filter.label,
        type: 'dropdown' as const,
        enabled: true,
        displayOrder,
        placeholder: `Select ${filter.label}`,
        options: filter.options || [],
        field: fieldMapping,
        multiple: filter.multiple,
        searchable: filter.multiple,
        apiEndpoint: filter.apiEndpoint,
        apiConfig: filter.apiConfig,
      });
    } else if (filter.type === 'dateRange') {
      combinedFields.push({
        id: filter.id,
        label: filter.label,
        type: 'dateRange' as const,
        enabled: true,
        displayOrder,
        options: [
          { value: 'today', label: 'Today' },
          { value: 'last7Days', label: 'Last 7 Days' },
          { value: 'last30Days', label: 'Last 30 Days' },
          { value: 'custom', label: 'Custom Range' },
        ],
        field: fieldMapping,
      });
    } else if (filter.type === 'date') {
      combinedFields.push({
        id: filter.id,
        label: filter.label,
        type: 'date' as const,
        enabled: true,
        displayOrder,
        field: fieldMapping,
      });
    } else if (filter.type === 'slider') {
      const minValue = filter.min ?? 0;
      const maxValue = filter.max ?? 100;
      const maxCapValue = filter.maxCap ?? 150;
      combinedFields.push({
        id: filter.id,
        label: filter.label,
        type: 'slider' as const,
        enabled: true,
        displayOrder,
        min: minValue,
        max: maxValue,
        maxCap: maxCapValue,
        step: 1,
        defaultValue: [minValue, maxValue] as [number, number],
        showValue: true,
        field: filter.id,
        fields: {
          minField: 'matchPercentageMin',
          maxField: 'matchPercentageMax',
        },
      });
    }
  });

  // Single push for all search / date / slider filters
  if (combinedFields.length > 0) {
    filterGroups.push({
      id: 'search-filters',
      title: 'Search Filters',
      type: 'card',
      displayOrder: 1,
      enabled: true,
      collapsible: false,
      fields: combinedFields,
    });
  }

  // Lead Type and Dedupe Status Filters Group (Combined in one card)
  if (
    tabConfig.leadStatusFilters.length > 0 ||
    tabConfig.dedupeStatusFilters.length > 0 ||
    (tabConfig.quickFilters && tabConfig.quickFilters.length > 0)
  ) {
    const checkboxGroups = [];

    // Add Lead Type checkbox group
    if (tabConfig.leadStatusFilters.length > 0) {
      checkboxGroups.push({
        id: 'leadType',
        title: tabConfig.leadTypeText || 'Lead Type',
        enabled: true,
        displayOrder: 2,
        field: 'leadType',
        options: tabConfig.leadStatusFilters.map((status) => ({
          value: status.value,
          label: status.label,
          color: status.color,
          bgColor: status.bgColor,
          borderColor: status.color,
          rowBgColor: status.rowBgColor,
        })),
      });
    }

    // Add Dedupe Status checkbox group
    if (tabConfig.dedupeStatusFilters.length > 0) {
      checkboxGroups.push({
        id: 'dedupeStatus',
        title: tabConfig.dedupeStatusText || 'Dedupe Status',
        enabled: true,
        displayOrder: 1,
        field: 'dedupeStatus',
        options: tabConfig.dedupeStatusFilters.map((status) => ({
          value: status.value,
          label: status.label,
          color: status.color,
          bgColor: status.bgColor,
          borderColor: status.color,
        })),
      });
    }

    // Add quick filters checkbox group
    if (tabConfig.quickFilters && tabConfig.quickFilters.length > 0) {
      checkboxGroups.push({
        id: 'quickFilters',
        title: tabConfig.quickFilterText || 'Quick Filters',
        enabled: true,
        displayOrder: 3,
        field: 'quickFilters',
        options: tabConfig.quickFilters.map((filter) => ({
          value: filter.value,
          label: filter.label,
          color: filter.color,
          bgColor: filter.bgColor,
          borderColor: filter.color,
        })),
      });
    }

    filterGroups.push({
      id: 'lead-and-dedupe-status',
      title: 'Lead Type & Dedupe Status',
      type: 'checkbox-card',
      displayOrder: 3,
      enabled: true,
      collapsible: false,
      checkboxGroups,
      layout: {
        type: 'grid',
        minWidth: '250px',
        maxWidth: '600px',
        columns: 2,
      },
    });
  }

  return {
    enabled: true,
    filterLabel: 'Filters',
    applyButtonText: 'Apply Filters',
    clearButtonText: 'Clear All',
    filterGroups,
  };
};

/**
 * Build a complete PageConfig from TabConfiguration for backward compatibility
 */
export const buildPageConfig = (tabConfig: TabConfiguration | null): PageConfig => {
  // Default config structure
  const defaultConfig: PageConfig = {
    filterConfig: {
      enabled: true,
      filterLabel: 'Filters',
      applyButtonText: 'Apply Filters',
      clearButtonText: 'Clear All',
      filterGroups: [],
    },
    enableFilters: true,
    enableSelection: true,
    enableExpandableRows: true,
    columns: [],
    tabs: [
      { label: 'Referrals', dataKey: 'referrals', value: 0 },
      { label: 'Non-Referrals', dataKey: 'nonReferrals', value: 1 },
      { label: 'Processed to CC', dataKey: 'processedReferrals', value: 2 },
    ],
  };

  if (!tabConfig) {
    return defaultConfig;
  }

  // Convert Sitecore columns to table columns
  const tableColumns = convertToTableColumns(tabConfig.columns);

  // Build filter configuration
  const filterConfig = convertToFilterConfig(tabConfig);

  return {
    ...defaultConfig,
    columns: tableColumns,
    filterConfig,
    noDataMessage: tabConfig.noResultsText,
  };
};

/**
 * Get expanded view field configuration with header labels
 */
export const getExpandedViewConfig = (
  tabConfig: TabConfiguration | null,
  headerLabels?: {
    personalInfoHeaderText?: string;
    demographicsHeaderText?: string;
    caregiverInfoHeaderText?: string;
    trackingParamsHeaderText?: string;
    dedupAddParamsHeaderText?: string;
    commentsHeaderText?: string;
    resetFilterText?: string;
  }
) => {
  if (!tabConfig) {
    return {
      personal: [],
      demographics: [],
      caregiverInfo: [],
      trackingParams: [],
      dedupAddParams: [],
      personalInfoHeaderText: headerLabels?.personalInfoHeaderText || 'Personal Information',
      demographicsHeaderText: headerLabels?.demographicsHeaderText || 'Demographics',
      caregiverInfoHeaderText: headerLabels?.caregiverInfoHeaderText || 'Caregiver Information',
      trackingParamsHeaderText: headerLabels?.trackingParamsHeaderText || 'Tracking Parameters',
      dedupAddParamsHeaderText:
        headerLabels?.dedupAddParamsHeaderText || 'Deduplication Additional Parameters',
      commentsHeaderText: headerLabels?.commentsHeaderText || 'Comments',
      viewDetails: {
        href: '',
        text: '',
      },
      sendToCC: '',
      resetFilterText: headerLabels?.resetFilterText || 'Reset Filters',
    };
  }

  return {
    ...tabConfig.expandedFields,
    personalInfoHeaderText: headerLabels?.personalInfoHeaderText || 'Personal Information',
    demographicsHeaderText: headerLabels?.demographicsHeaderText || 'Demographics',
    caregiverInfoHeaderText: headerLabels?.caregiverInfoHeaderText || 'Caregiver Information',
    trackingParamsHeaderText: headerLabels?.trackingParamsHeaderText || 'Tracking Parameters',
    dedupAddParamsHeaderText:
      headerLabels?.dedupAddParamsHeaderText || 'Deduplication Additional Parameters',
    commentsHeaderText: headerLabels?.commentsHeaderText || 'Comments',
    viewDetails: tabConfig.viewDetails,
    sendToCC: tabConfig.sendToCC,
    resetFilterText: headerLabels?.resetFilterText || 'Reset Filters',
  };
};
